var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { alarmNotificationsApi, } from "@api/enpoints/alarm-notifications-api";
import { useDebounce } from "@ui/utils/useDebounce";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { setAlarms, setLoading, updateAlarmNotification, setPaginationInfo, setPageNumber, setPageSize, } from "@reducers/alarm-notifications/slice/alarm-notifications-slice";
// Map API response to AlarmData format
const mapAlarmNotificationToAlarmData = (alarm) => {
    var _a;
    const dateStr = formatUtcToLocalDate(alarm.alarmStartDateTime);
    const timeStr = formatUtcToLocalTime(alarm.alarmStartDateTime);
    // Map alarm type enum to string
    const alarmTypeMap = {
        1: "Temperature Low",
        2: "Temperature High",
        3: "Fat Replacement",
        4: "Open Door",
        5: "No Node Communication",
        6: "No AP Communication",
        7: "Temperature Reading Delay",
    };
    // The limit alarms of new-hardware sensors (8 / 9) name their sensor, e.g. "Humidity High".
    const sensorNames = {
        1: "Internal Temperature",
        2: "External Temperature",
        3: "Humidity",
        4: "CO2",
    };
    const sensorName = alarm.sensorType != null ? sensorNames[alarm.sensorType] : undefined;
    const alarmTypeName = alarm.alarmType === 8 || alarm.alarmType === 9
        ? `${sensorName !== null && sensorName !== void 0 ? sensorName : "Sensor"} ${alarm.alarmType === 8 ? "Low" : "High"}`
        : alarmTypeMap[alarm.alarmType] || "Unknown Alarm";
    // Format location (AccessPointName - NodeName)
    const location = alarm.accessPointName && alarm.nodeName
        ? `${alarm.accessPointName} - ${alarm.nodeName}`
        : alarm.accessPointName || alarm.nodeName || "Unknown Location";
    // Node display: name if available, otherwise UID
    const nodeName = ((_a = alarm.nodeName) === null || _a === void 0 ? void 0 : _a.trim()) || alarm.nodeUid || "";
    // Format value (temperature/threshold)
    const value = alarm.temperature != null ? `${alarm.temperature.toFixed(1)}` : "N/A";
    return {
        id: alarm.id,
        location,
        nodeName,
        nodeUid: alarm.nodeUid || "",
        dateTime: `${dateStr} ${timeStr}`,
        date: dateStr,
        time: timeStr,
        alarmType: alarmTypeName,
        value,
        handledBy: alarm.acknowledgedByName || alarm.acknowledgedBy || "",
        explanation: alarm.explanation || "",
        handlingStatus: alarm.isAlarmAcknowledged
            ? "acknowledged"
            : "unacknowledged",
        isActive: !alarm.alarmEndDateTime, // Active if no end date
    };
};
export const useAlarmsFilter = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["common"]);
    const { alarms: reduxAlarms, loading: reduxLoading } = useSelector((state) => state.alarmNotifications);
    const paginationInfo = useSelector((state) => state.alarmNotifications.paginationInfo);
    const currentPageNumber = useSelector((state) => state.alarmNotifications.currentPageNumber);
    const currentPageSize = useSelector((state) => state.alarmNotifications.currentPageSize);
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const prevFiltersRef = useRef(null);
    const prevSearchRef = useRef(null);
    const prevPaginationRef = useRef(null);
    const hasInitialized = useRef(false);
    const fetchAlarms = (filterParams, search) => __awaiter(void 0, void 0, void 0, function* () {
        dispatch(setLoading(true));
        try {
            // Build API params from filters
            const apiParams = {
                pageNumber: currentPageNumber,
                pageSize: currentPageSize,
            };
            // Map filter params to API params
            if (filterParams) {
                if (filterParams.accessPointUid) {
                    apiParams.accessPointUid = Array.isArray(filterParams.accessPointUid)
                        ? filterParams.accessPointUid[0]
                        : filterParams.accessPointUid;
                }
                if (filterParams.nodeUid) {
                    apiParams.nodeUid = Array.isArray(filterParams.nodeUid)
                        ? filterParams.nodeUid[0]
                        : filterParams.nodeUid;
                }
                if (filterParams.handlingStatus) {
                    const statuses = Array.isArray(filterParams.handlingStatus)
                        ? filterParams.handlingStatus
                        : [filterParams.handlingStatus];
                    if (statuses.length === 1) {
                        apiParams.isAcknowledged = statuses[0] === "acknowledged";
                    }
                    // Both or neither selected → no filter, show all
                }
                if (filterParams.acknowledgedBy &&
                    (Array.isArray(filterParams.acknowledgedBy)
                        ? filterParams.acknowledgedBy.length > 0
                        : filterParams.acknowledgedBy !== "")) {
                    apiParams.acknowledgedBy = Array.isArray(filterParams.acknowledgedBy)
                        ? filterParams.acknowledgedBy
                        : [filterParams.acknowledgedBy];
                }
                if (filterParams.alarmType) {
                    const raw = Array.isArray(filterParams.alarmType)
                        ? filterParams.alarmType
                        : [filterParams.alarmType];
                    const typeInts = raw
                        .map((v) => parseInt(v, 10))
                        .filter((n) => !isNaN(n));
                    if (typeInts.length > 0) {
                        apiParams.alarmTypes = typeInts;
                    }
                }
            }
            if (search) {
                apiParams.search = search;
            }
            const response = yield alarmNotificationsApi.getAlarmNotifications(apiParams);
            if (response.isSuccess && response.data) {
                dispatch(setAlarms(response.data.alarms));
                dispatch(setPaginationInfo({
                    totalCount: response.data.totalCount,
                    pageNumber: response.data.pageNumber,
                    pageSize: response.data.pageSize,
                    totalPages: response.data.totalPages,
                }));
            }
            else {
                toast.error(t("common:toasts.failedToFetchAlarms"));
                dispatch(setAlarms([]));
            }
        }
        catch (error) {
            console.error("Error fetching alarms:", error);
            toast.error(t("common:toasts.errorLoadingAlarms"));
            dispatch(setAlarms([]));
        }
        finally {
            dispatch(setLoading(false));
        }
    });
    // Fetch alarms when filters or search change
    useEffect(() => {
        const filtersKey = JSON.stringify(filters);
        const searchKey = debouncedSearchTerm || "";
        const paginationKey = `${currentPageNumber}-${currentPageSize}`;
        // Always fetch on initial mount, then only if values actually changed
        if (!hasInitialized.current ||
            prevFiltersRef.current !== filtersKey ||
            prevSearchRef.current !== searchKey ||
            prevPaginationRef.current !== paginationKey) {
            prevFiltersRef.current = filtersKey;
            prevSearchRef.current = searchKey;
            prevPaginationRef.current = paginationKey;
            hasInitialized.current = true;
            fetchAlarms(filters, debouncedSearchTerm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, debouncedSearchTerm, currentPageNumber, currentPageSize]);
    const handleFilterChange = (newFilters) => {
        dispatch(setPageNumber(1));
        setFilters(newFilters);
    };
    const handleSearchChange = (event) => {
        dispatch(setPageNumber(1));
        setSearchTerm(event.target.value);
    };
    const handleClearFilters = () => {
        dispatch(setPageNumber(1));
        setFilters({});
        setSearchTerm("");
    };
    const handleToggleActive = (alarm) => {
        const alarmDto = reduxAlarms.find((a) => a.id === alarm.id);
        if (alarmDto) {
            dispatch(updateAlarmNotification({
                id: alarm.id,
                isAlarmAcknowledged: alarmDto.isAlarmAcknowledged,
            }));
        }
    };
    const handleToggleStatus = (alarm) => __awaiter(void 0, void 0, void 0, function* () {
        if (alarm.handlingStatus === "acknowledged") {
            return;
        }
        try {
            const response = yield alarmNotificationsApi.acknowledgeAlarm(alarm.id);
            if (response.isSuccess) {
                dispatch(updateAlarmNotification({
                    id: alarm.id,
                    isAlarmAcknowledged: true,
                    alarmEndDateTime: new Date().toISOString(),
                }));
                toast.success(t("common:toasts.alarmAcknowledgedSuccessfully"));
            }
            else {
                toast.error(t("common:toasts.failedToAcknowledgeAlarm"));
            }
        }
        catch (error) {
            console.error("Error acknowledging alarm:", error);
            toast.error(t("common:toasts.errorAcknowledgingAlarm"));
        }
    });
    const filteredAlarms = useMemo(() => reduxAlarms.map(mapAlarmNotificationToAlarmData), [reduxAlarms]);
    const handlePageChange = (pageNumber) => {
        dispatch(setPageNumber(pageNumber));
    };
    const handlePageSizeChange = (pageSize) => {
        dispatch(setPageSize(pageSize));
    };
    return {
        alarms: filteredAlarms,
        loading: reduxLoading,
        searchTerm,
        filters,
        paginationInfo,
        currentPageNumber,
        currentPageSize,
        handleFilterChange,
        handleSearchChange,
        handleClearFilters,
        handleToggleActive,
        handleToggleStatus,
        handlePageChange,
        handlePageSizeChange,
        fetchAlarms,
    };
};
